<template>
    <settings-wrapper title="User settings" section="Table access">
        <v-skeleton-loader v-if="!allLoaded" class="mx-auto" type="table"></v-skeleton-loader>
        <template v-else>
            <div v-if="userInfo.sf_user_type === tableUserModes.NONE" class="d-flex">
                <v-alert type="error" text>
                    You don't have access to any spaces with tables enabled. Your credentials will be generated once you join a space with tables or they are
                    enabled in one of your spaces.
                </v-alert>
            </div>

            <v-row v-else>
                <v-col cols="5">
                    <v-card>
                        <v-card-title>
                            Connection mode
                            <v-btn icon small class="ml-2" href="https://docs.nuvolos.com/concepts/data-integration#connection-modes" target="_blank"></v-btn>
                        </v-card-title>
                        <v-card-text>
                            <table-user-mode-switch></table-user-mode-switch>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col>
                    <v-card v-if="userInfo.sf_user_type === tableUserModes.SERVICE">
                        <v-card-title>RSA public key</v-card-title>
                        <v-card-text class="d-flex align-center">
                            <v-text-field v-model="publicKey" class="mr-2" :disabled="loading" outlined dense hide-details></v-text-field>
                            <copy-to-clipboard
                                :textToCopy="publicKey"
                                buttonClass="caption "
                                buttonColor="secondary"
                                buttonName="copy to clipboard"
                                :iconButton="true"
                                :disabled="loading"></copy-to-clipboard>
                            <v-btn color="primary" @click="rotateRSA()" :loading="loading" class="ml-3">Rotate RSA key</v-btn>
                        </v-card-text>
                    </v-card>
                    <v-card v-else>
                        <v-card-title>Access token</v-card-title>
                        <v-card-text class="d-flex align-center">
                            <v-text-field
                                :append-icon="show ? 'visibility' : 'visibility_off'"
                                :type="show ? 'text' : 'password'"
                                :value="snowflakeTokens.access_token"
                                class="input-group--focused mr-2"
                                @click:append="show = !show"
                                :disabled="loading"
                                outlined
                                dense
                                hide-details></v-text-field>
                            <copy-to-clipboard
                                :textToCopy="snowflakeTokens.access_token"
                                buttonClass="caption "
                                buttonColor="secondary"
                                buttonName="copy to clipboard"
                                :iconButton="true"
                                :disabled="loading"></copy-to-clipboard>
                            <v-btn color="primary" @click="generateToken()" :loading="loading" class="ml-3">Generate New Token</v-btn>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </settings-wrapper>
</template>

<script>
import { mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
const CopyToClipboard = () => import('@/components/CopyToClipboard')
const TableUserModeSwitch = () => import('@/modules/user/components/TableUserModeSwitch.vue')

export default {
    name: 'UserSettingsTableAccess',
    mixins: [enumsData],
    components: {
        SettingsWrapper,
        CopyToClipboard,
        TableUserModeSwitch
    },
    data() {
        return {
            dialog: false,
            token: '',
            show: false,
            loading: false,
            publicKey: ''
        }
    },
    methods: {
        async generateToken() {
            this.loading = true
            try {
                await this.$axios.post('/tokens/snowflake_access')
                this.$store.dispatch('userStore/fetchSnowflakeTokens')
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'New token has been generated!',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to generate new token',
                    snackBarTimeout: 10000,
                    snackBarIcon: 'error'
                })
            }
            this.loading = false
        },
        async rotateRSA() {
            this.loading = true
            try {
                const { data } = await this.$axios.post('/tokens/snowflake_rsa_key')
                this.publicKey = data.public_key
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'RSA key has been rotated!',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to rotate RSA key',
                    snackBarTimeout: 10000,
                    snackBarIcon: 'error'
                })
            }
            this.loading = false
        },
        async fetchRSA() {
            try {
                const { data } = await this.$axios.get('/tokens/snowflake_rsa_key')
                this.publicKey = data.public_key
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to get RSA key',
                    snackBarTimeout: 10000,
                    snackBarIcon: 'error'
                })
            }
        }
    },
    computed: {
        ...mapState('userStore', ['snowflakeTokens', 'fetchingSnowflakeTokens']),
        ...mapState(['userInfo', 'fetchingUserInfo']),
        allLoaded() {
            return !this.fetchingSnowflakeTokens && !this.fetchingUserInfo
        }
    },
    watch: {
        userInfo: {
            handler(info) {
                if (info.sf_user_type === this.tableUserModes.NONE) return

                if (info.sf_user_type === this.tableUserModes.SERVICE) {
                    this.fetchRSA()
                } else {
                    this.$store.dispatch('userStore/fetchSnowflakeTokens')
                }
            },
            immediate: true
        }
    }
}
</script>
